import React, { useEffect, useRef, useState } from 'react';
import { platesApi } from './api';
import { PLATE_STATE_OPTIONS, FUEL_OPTIONS, VEHICLE_USE_OPTIONS, VEHICLE_TYPE_OPTIONS, normalizeVehicleType } from './platesConfig';
import ModalShell from '../insurance/ModalShell';

// Normaliza una fecha (ISO o YYYY-MM-DD) a YYYY-MM-DD para <input type="date">
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * Corrección tipográfica: edita los valores de la MISMA placa con todo
 * precargado. No reenvía correo ni reprocesa con IA. Adjuntar es opcional.
 */
export default function CorrectionForm({ visible, onHide, plate, toast, onDone }) {
  const [plateNumber, setPlateNumber] = useState('');
  const [plateState, setPlateState] = useState('CDMX');
  const [folio, setFolio] = useState('');
  const [vinCard, setVinCard] = useState('');
  const [motorNumber, setMotorNumber] = useState('');
  const [fuelType, setFuelType] = useState('Gasolina');
  const [vehicleUse, setVehicleUse] = useState('Particular');
  const [vehicleBrand, setVehicleBrand] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleType, setVehicleType] = useState('Auto');
  const [ownerName, setOwnerName] = useState('');
  const [ownerRfc, setOwnerRfc] = useState('');
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [cadenceMonths, setCadenceMonths] = useState(12);
  const [gestorName, setGestorName] = useState('');
  const [gestorEmail, setGestorEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Precargar con los valores actuales de la placa al abrir
  useEffect(() => {
    if (!visible || !plate) return;
    setPlateNumber(plate.plate_number || '');
    setPlateState(plate.plate_state || 'CDMX');
    setFolio(plate.folio || '');
    setVinCard(plate.vin_card || '');
    setMotorNumber(plate.motor_number || '');
    setFuelType(plate.fuel_type || 'Gasolina');
    setVehicleUse(plate.vehicle_use || 'Particular');
    setVehicleBrand(plate.vehicle_brand_card || '');
    setVehicleModel(plate.vehicle_model_card || '');
    setVehicleYear(plate.vehicle_year_card ?? '');
    setVehicleType(normalizeVehicleType(plate.vehicle_type_card) || 'Auto');
    setOwnerName(plate.owner_name || '');
    setOwnerRfc(plate.owner_rfc || '');
    setIssuedAt(toDateInput(plate.issued_at));
    setExpiresAt(toDateInput(plate.expires_at));
    setCadenceMonths(plate.renewal_cadence_months || 12);
    setGestorName(plate.gestor_name || '');
    setGestorEmail(plate.gestor_email || '');
    setAttachments([]);
  }, [visible, plate]);

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []).map((file) => ({
      id: 'c-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      file,
      nombre: file.name,
    }));
    setAttachments((prev) => [...prev, ...list]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSave = !!plateNumber && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('plate_number', plateNumber);
      fd.append('plate_state', plateState);
      if (folio !== '') fd.append('folio', folio);
      if (vinCard !== '') fd.append('vin_card', vinCard);
      if (motorNumber !== '') fd.append('motor_number', motorNumber);
      fd.append('fuel_type', fuelType);
      fd.append('vehicle_use', vehicleUse);
      if (vehicleBrand !== '') fd.append('vehicle_brand_card', vehicleBrand);
      if (vehicleModel !== '') fd.append('vehicle_model_card', vehicleModel);
      if (vehicleYear !== '') fd.append('vehicle_year_card', vehicleYear);
      fd.append('vehicle_type_card', vehicleType);
      if (ownerName !== '') fd.append('owner_name', ownerName);
      if (ownerRfc !== '') fd.append('owner_rfc', ownerRfc);
      if (issuedAt) fd.append('issued_at', issuedAt);
      if (expiresAt) fd.append('expires_at', expiresAt);
      fd.append('renewal_cadence_months', cadenceMonths);
      if (gestorName !== '') fd.append('gestor_name', gestorName);
      if (gestorEmail !== '') fd.append('gestor_email', gestorEmail);
      attachments.forEach((a, i) => {
        fd.append(`attachments[${i}][file]`, a.file);
        fd.append(`attachments[${i}][label]`, 'corrección');
      });

      const res = await platesApi.correct(plate.id, fd);
      if (res?.data?.success) {
        toast?.current?.show?.({ severity: 'success', summary: 'Corrección aplicada', life: 3500 });
        onDone?.();
        onHide?.();
      }
    } catch (e) {
      toast?.current?.show?.({ severity: 'error', summary: 'No se pudo corregir', detail: e?.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell visible={visible} onClose={saving ? () => {} : onHide} stacked width={620}>
      <button className="modal-close" onClick={onHide} disabled={saving}><i className="pi pi-times" /></button>

      <div style={{ padding: '30px 32px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Corrección tipográfica</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pei-text)', letterSpacing: '-0.01em', marginBottom: 6 }}>
          Editar datos de la placa
        </div>
        <div style={{ fontSize: 13, color: 'var(--pei-text-helper)' }}>
          Edita directamente los valores. No se reenvía correo ni se reprocesa con IA.
        </div>
      </div>

      <div className="scrollable" style={{ padding: '8px 32px 16px', flex: 1, minHeight: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Número de placa</div>
            <input className="pei-input" value={plateNumber} onChange={(e) => setPlateNumber(e.target.value.toUpperCase())} />
          </div>
          <div>
            <div className="pei-label">Estado</div>
            <select className="pei-input" value={plateState} onChange={(e) => setPlateState(e.target.value)}>
              {PLATE_STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Folio de la tarjeta</div>
            <input className="pei-input" value={folio} onChange={(e) => setFolio(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">VIN (tarjeta)</div>
            <input className="pei-input" value={vinCard} onChange={(e) => setVinCard(e.target.value.toUpperCase())} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Número de motor</div>
            <input className="pei-input" value={motorNumber} onChange={(e) => setMotorNumber(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">Combustible</div>
            <select className="pei-input" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
              {FUEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Uso del vehículo</div>
            <select className="pei-input" value={vehicleUse} onChange={(e) => setVehicleUse(e.target.value)}>
              {VEHICLE_USE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div className="pei-label">Tipo de vehículo</div>
            <select className="pei-input" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
              {VEHICLE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Marca (tarjeta)</div>
            <input className="pei-input" value={vehicleBrand} onChange={(e) => setVehicleBrand(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">Modelo (tarjeta)</div>
            <input className="pei-input" value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">Año (tarjeta)</div>
            <input className="pei-input" type="number" min={1950} max={2100} value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Nombre del titular</div>
            <input className="pei-input" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">RFC del titular</div>
            <input className="pei-input" value={ownerRfc} onChange={(e) => setOwnerRfc(e.target.value.toUpperCase())} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div className="pei-label">Fecha de emisión</div>
            <input className="pei-input" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">Fecha de vencimiento</div>
            <input className="pei-input" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <div>
            <div className="pei-label">Cadencia de renovación (meses)</div>
            <input className="pei-input" type="number" min={1} max={120} value={cadenceMonths} onChange={(e) => setCadenceMonths(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <div>
            <div className="pei-label">Nombre del gestor</div>
            <input className="pei-input" value={gestorName} onChange={(e) => setGestorName(e.target.value)} />
          </div>
          <div>
            <div className="pei-label">Correo del gestor</div>
            <input className="pei-input" type="email" value={gestorEmail} onChange={(e) => setGestorEmail(e.target.value)} />
          </div>
        </div>

        {/* Adjuntos opcionales — solo se guardan, no se leen con IA */}
        <div className="pei-label" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <i className="pi pi-paperclip" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
          Adjuntos opcionales
          <span style={{ color: 'var(--pei-text-helper)', fontWeight: 500, letterSpacing: 0, textTransform: 'none', marginLeft: 4, fontSize: 11 }}>
            · no se procesan con IA
          </span>
        </div>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFiles} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {attachments.map((f) => (
            <span key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 8px 6px 10px', background: 'white', border: '1px solid var(--pei-gray-5)', borderRadius: 999, fontSize: 12 }}>
              <i className="pi pi-file" style={{ fontSize: 11, color: 'var(--pei-cyan)' }} />
              <span style={{ color: 'var(--pei-text)', fontWeight: 500 }}>{f.nombre}</span>
              <span onClick={() => setAttachments(attachments.filter((a) => a.id !== f.id))} style={{ cursor: 'pointer', color: 'var(--pei-text-helper)', padding: '0 4px', fontSize: 14 }}>×</span>
            </span>
          ))}
          <button className="chip-add" onClick={() => fileInputRef.current?.click()} type="button">
            <i className="pi pi-paperclip" style={{ fontSize: 11 }} /> Adjuntar
          </button>
        </div>
      </div>

      <div style={{ padding: '14px 32px 22px', borderTop: '1px solid var(--pei-gray-5)', display: 'flex', gap: 10 }}>
        <button className="pei-btn pei-btn-ghost" onClick={onHide} disabled={saving} style={{ flex: '0 0 auto' }}>Cancelar</button>
        <button className="pei-btn pei-btn-primary floor-shadow" onClick={handleSave} disabled={!canSave} style={{ flex: 1 }}>
          {saving ? 'Guardando…' : 'Guardar corrección'} <i className="pi pi-check" style={{ fontSize: 12 }} />
        </button>
      </div>
    </ModalShell>
  );
}
